import { CheckCircle2, Loader2, Pause, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  formatDateTime,
  formatDuration,
  formatRelativeDate,
} from "@/lib/formatters";
import { getRunStatusColor } from "@/lib/constants";
import type { JobRun } from "@/api/types";
import { getPappersStatsView } from "@/lib/pappersStats";
import { PappersRunStats } from "./PappersRunStats";
import { EmailSyncRunStats } from "./EmailSyncRunStats";
import { isEmailSyncJob } from "@/lib/email";

interface JobRunsTimelineProps {
  runs: JobRun[];
  onPick: (run: JobRun) => void;
  selectedRunId?: number | null;
}

export function JobRunsTimeline({
  runs,
  onPick,
  selectedRunId,
}: JobRunsTimelineProps) {
  if (runs.length === 0) {
    return (
      <div className="rounded-plane border border-dashed border-slate-200 px-4 py-12 text-center text-sm text-slate-400">
        No runs yet
      </div>
    );
  }

  return (
    <ol className="space-y-2">
      {runs.map((run) => (
        <li key={run.id}>
          <button
            type="button"
            onClick={() => onPick(run)}
            className={cn(
              "flex w-full items-start gap-3 rounded-xl border bg-white px-4 py-3 text-left transition-colors duration-150",
              selectedRunId === run.id
                ? "border-indigo-300 bg-indigo-50/40"
                : "border-slate-200/60 hover:border-slate-300 hover:bg-slate-50/60",
            )}
          >
            <RunStatusIcon status={run.status} />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-slate-900">
                    Run #{run.id}
                  </span>
                  <RunStatusPill status={run.status} />
                </div>
                <span className="text-xs text-slate-400">
                  {formatRelativeDate(run.started_at)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                <span title={formatDateTime(run.started_at)}>
                  Started {formatDateTime(run.started_at, "MMM d, HH:mm")}
                </span>
                {run.duration_seconds != null && (
                  <>
                    <span className="text-slate-300">·</span>
                    <span>{formatDuration(run.duration_seconds)}</span>
                  </>
                )}
                <span className="text-slate-300">·</span>
                <span className="capitalize">
                  triggered by {run.triggered_by}
                </span>
              </div>
              {renderStats(run)}
              {run.error && (
                <div className="line-clamp-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-700">
                  {run.error}
                </div>
              )}
            </div>
          </button>
        </li>
      ))}
    </ol>
  );
}

function RunStatusIcon({ status }: { status: string }) {
  const key = status.toLowerCase();
  if (key === "running") {
    return (
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50">
        <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
      </span>
    );
  }
  if (key === "succeeded" || key === "success") {
    return (
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50">
        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
      </span>
    );
  }
  if (key === "failed") {
    return (
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-50">
        <XCircle className="h-3 w-3 text-rose-500" />
      </span>
    );
  }
  if (key === "partial") {
    return (
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange-50">
        <XCircle className="h-3 w-3 text-orange-500" />
      </span>
    );
  }
  if (key === "paused" || key === "cancelled") {
    return (
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100">
        <Pause className="h-3 w-3 text-slate-500" />
      </span>
    );
  }
  return (
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
    </span>
  );
}

function RunStatusPill({ status }: { status: string }) {
  const colors = getRunStatusColor(status);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-medium uppercase tracking-wider",
        colors.bg,
        colors.text,
      )}
    >
      {status}
    </span>
  );
}

function renderStats(run: JobRun) {
  const pappers = getPappersStatsView(run);
  if (pappers) return <PappersRunStats stats={pappers} compact />;
  if (isEmailSyncJob(run.job_id)) return <EmailSyncRunStats stats={run.stats} compact />;
  const stats = run.stats;
  const entries = Object.entries(stats ?? {});
  if (entries.length === 0) return null;
  // Show up to 4 numeric stats inline as chips.
  const chips = entries
    .filter(([, v]) => typeof v === "number" || typeof v === "string")
    .slice(0, 4);
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map(([key, value]) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600"
        >
          <span className="font-medium text-slate-700">{String(value)}</span>
          <span className="text-slate-400">{humanize(key)}</span>
        </span>
      ))}
    </div>
  );
}

function humanize(s: string): string {
  return s.replace(/_/g, " ").toLowerCase();
}
